import { useMemo } from 'react';
import { useCampaignSettingsGoogleAdsStore } from '@/store/campaignSettingsGoogleAdsStore';
import { useCampaignSettingsMetaAdsStore } from '@/store/campaignSettingsMetaAdsStore';

/**
 * Phase 1c — cross-platform optimizer candidate aggregator.
 *
 * Walks both Google and Meta settings stores and returns one tagged
 * entry per flouri-optimized campaign that's eligible to participate
 * in the simplex allocator. Replaces Google's per-store
 * `getOptimizedCampaigns()` selector for callers that need a mixed pool
 * (BudgetOptimizerSimple — request builder, totalBudget auto-sync).
 *
 * Eligibility per platform:
 * - Google: `isOptimizationEnabled && campaignStatus === 'ENABLED'`
 *   (matches the existing Google-store `getOptimizedCampaigns` filter).
 * - Meta: `isOptimizationEnabled && campaignStatus === 'ACTIVE' && isCbo && isDailyBudget`.
 *   ACTIVE is Meta's serving-state analogue of Google's ENABLED. CBO +
 *   daily are Phase 1 eligibility (§A in
 *   docs/META_BUDGET_OPTIMIZATION_ACTIVATION.md) — ABO + lifetime are
 *   filtered out at the FlowerButton too, but the request-builder
 *   re-checks here as defense in depth against a stale toggle that
 *   survived a budget-model change in Meta.
 *
 * Same tagged shape on both sides — `platform` tells callers which
 * store to dispatch the response back to (`setManualBudget` on the
 * right store keyed by campaign_id + platform). The cross-platform
 * `campaignName` is included for the dev-mode preview panel.
 */

export type OptimizerPlatform = 'google_ads' | 'meta_ads';

export interface OptimizedCampaignEntry {
  campaignId: string;
  platform: OptimizerPlatform;
  campaignName: string;
  // Server-baseline daily budget (major units). Used as the "current"
  // value when the user has no staged edit.
  currentBudget: number;
  // User-staged budget edit (major units) or null.
  manualBudget: number | null;
}

export interface OptimizedCampaignsResult {
  entries: OptimizedCampaignEntry[];
  /**
   * Sum of `manualBudget ?? currentBudget` across both platforms, cent-
   * rounded. Replaces the Google-only `getTotalCurrentBudget()` for
   * BudgetOptimizerSimple's auto-sync — same coalescing semantics so
   * the displayed total + the auto-sync target reflect what would
   * actually be saved.
   */
  totalCurrentBudget: number;
}

export function useOptimizedCampaigns(): OptimizedCampaignsResult {
  const googleCampaigns = useCampaignSettingsGoogleAdsStore((s) => s.campaignSettings);
  const metaCampaigns = useCampaignSettingsMetaAdsStore((s) => s.campaignSettings);

  return useMemo(() => {
    const entries: OptimizedCampaignEntry[] = [];

    for (const settings of googleCampaigns.values()) {
      if (!settings.isOptimizationEnabled) continue;
      if (settings.campaignStatus !== 'ENABLED') continue;
      entries.push({
        campaignId: settings.campaignId,
        platform: 'google_ads',
        campaignName: settings.manualName || settings.currentName,
        currentBudget: settings.currentBudget,
        manualBudget: settings.manualBudget,
      });
    }

    for (const settings of metaCampaigns.values()) {
      if (!settings.isOptimizationEnabled) continue;
      if (settings.campaignStatus !== 'ACTIVE') continue;
      // Defense in depth — MetaCampaignCard's eligibility filter
      // already prevents staging this state, but a stale toggle that
      // survived a CBO→ABO change in Meta would still be in the
      // store. Drop it here so it doesn't reach the simplex request.
      if (!settings.isCbo || !settings.isDailyBudget) continue;
      entries.push({
        campaignId: settings.campaignId,
        platform: 'meta_ads',
        campaignName: settings.manualName || settings.currentName,
        currentBudget: settings.currentBudget,
        manualBudget: settings.manualBudget,
      });
    }

    const rawTotal = entries.reduce(
      (sum, e) => sum + (e.manualBudget ?? e.currentBudget),
      0,
    );
    const totalCurrentBudget = Math.round(rawTotal * 100) / 100;

    return { entries, totalCurrentBudget };
  }, [googleCampaigns, metaCampaigns]);
}
